import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import { Box, Grid, Typography, useTheme } from '@mui/material';
import { useEffect, useState } from 'react';
import CoursesApi from '../../../../../apis/CoursesApi';
import UnexpectedError from '../../../../../components/UnexpectedError/UnexpectedError';
import { IntroModuleDto } from '../../../../../generated-types/introModuleDto';
import useApi from '../../../../../lib/useApi';
import BaseModuleProps from '../../../lib/BaseModuleProps';

/**
 * The first module to introduce the user to the course
 */
const IntroModule = ({
  moduleId,
  setIsLoading,
  setComplete,
}: BaseModuleProps) => {
  const coursesApi = useApi(CoursesApi);
  const [showError, setShowError] = useState(false);
  const [introModule, setIntroModule] = useState<IntroModuleDto>();
  const theme = useTheme();

  useEffect(() => {
    let isActive = true;
    setIsLoading(true);
    const init = async () => {
      try {
        const module = await coursesApi.GetModule<IntroModuleDto>(moduleId);
        if (isActive) setIntroModule(module);

        // Set the intro module complete, if needed
        if (isActive) {
          setComplete();
        }
      } catch (e) {
        setShowError(true);
      } finally {
        if (isActive)
          // Turn off loading
          setIsLoading(false);
      }
    };

    init();
    return () => {
      isActive = false;
    };
  }, [coursesApi, moduleId, setComplete, setIsLoading]);

  return (
    <Box height="100%" width="100%" overflow="auto">
      {showError ? (
        <UnexpectedError errorContext="the intro module" />
      ) : (
        <>
          <Typography
            variant="h2"
            textAlign="center"
            marginTop=".25em"
            data-testid="intro-module-course-title"
          >
            {introModule?.courseTitle}
          </Typography>
          <Typography
            variant="h5"
            textAlign="center"
            color={theme.palette.text.secondary}
          >
            {introModule?.courseSubtitle}
          </Typography>
          <Box display="flex" gap="1em" flexDirection="column" padding="1em">
            {introModule?.modules.map((m, i) => (
              <Grid key={i} container spacing={2}>
                <Grid item xs={12} sm={5}>
                  <Box
                    padding=".2em"
                    height="100%"
                    width="100%"
                    sx={{
                      borderRadius: '.3em',
                      '@keyframes animatedBorder': {
                        '0%': {
                          backgroundPosition: '0% 50%',
                        },
                        '50%': {
                          backgroundPosition: '100% 50%',
                        },
                        '100%': {
                          backgroundPosition: '0% 50%',
                        },
                      },
                      background:
                        'linear-gradient(-70deg, #1976D2, #191AD2, #19D2D1)',
                      backgroundSize: '400% 400%',
                      animation: 'animatedBorder 10s ease infinite',
                    }}
                  >
                    <Box
                      padding="1em"
                      display="flex"
                      flexDirection="column"
                      justifyContent="center"
                      sx={{
                        borderRadius: '.3em',
                        backgroundColor: theme.palette.background.paper,
                        height: '100%',
                      }}
                    >
                      <Typography
                        fontSize="2em"
                        fontWeight="bold"
                        lineHeight="normal"
                      >
                        Module {m.moduleNumber}
                      </Typography>
                      <Typography fontSize="1.15em">{m.moduleTitle}</Typography>
                      <Typography color={theme.palette.text.secondary}>
                        {m.moduleSubtitle}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={7}>
                  <Box
                    display="flex"
                    flexDirection="column"
                    gap=".2em"
                    height="100%"
                    width="100%"
                    marginBottom="1em"
                    sx={{
                      marginTop: {
                        xs: '-.5em',
                        sm: 0,
                      },
                    }}
                  >
                    <Typography fontWeight="bold" fontSize="1.5em" gutterBottom>
                      Learning Objectives
                    </Typography>
                    {m.learningObjectives.map((o, i) => (
                      <Box key={i} display="flex">
                        <CheckCircleOutlineRoundedIcon color="success" />
                        <Typography marginLeft=".75em">{o}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Grid>
              </Grid>
            ))}
          </Box>
        </>
      )}
    </Box>
  );
};

export default IntroModule;
